// BUILD-046 section D (Owner direction of 2026-10-21): the state of the Presets control of ONE tab for ONE person.
//
// The page tells this hook who the person is, which tab is open, what the view is right now (`current`, the criteria the page is showing) and how to apply a set of criteria to the
// page. The hook keeps the person's presets for the tab (presetsStore.ts), applies the DEFAULT preset the first time the tab is opened in this session (once per person and tab - a
// manual change afterwards is never undone by it), remembers which preset is applied, and says whether the view is still that preset or Modified. A manual change never saves or
// overwrites anything: a preset changes only through Save, Update, Rename, Delete or Set default, each of which is a click and each of which reports what it did.
//
// Erasable TypeScript only (a React hook over the pure models).
import { useCallback, useEffect, useRef, useState } from "react";
import { EMPTY_BOOK, addPreset, deletePreset, editPreset, movePreset, presetOf, renamePreset, sameCriteria, setDefault, updatePreset } from "./presetsModel";
import type { MoveWhere, Preset, PresetBook, PresetCriteria, PresetTab } from "./presetsModel";
import { browserStorage, loadBook, presetsKey, saveBook } from "./presetsStore";
import type { KeyValueStorage } from "./presetsStore";

export interface UsePresetsArgs {
  /** The signed-in person (employee id); null until known - nothing is read or written then. */
  userId: string | null;
  tab: PresetTab;
  /** What the page is showing right now. */
  current: PresetCriteria;
  /** Makes the page show these criteria (its own setters; the page still only shows rows the server returned). */
  apply: (criteria: PresetCriteria) => void;
  storage?: KeyValueStorage | null;
  now?: () => Date;
  newId?: () => string;
}

export interface PresetsController {
  presets: readonly Preset[];
  defaultId: string | null;
  appliedId: string | null;
  appliedName: string | null;
  /** True while a preset is applied and the view no longer matches it. */
  modified: boolean;
  /** The last action's result, in words (success or the reason it did not happen). Empty when none. */
  message: string;
  canSave: boolean;
  applyPreset: (id: string) => void;
  savePreset: (name: string) => boolean;
  updateApplied: () => void;
  resetToApplied: () => void;
  renamePresetTo: (id: string, name: string) => boolean;
  /**
   * Owner direction 2026-10-23: saves the changes of the Edit preset panel to the SAME preset (same id, same place in the list, same default mark). When that preset is the one applied, the view shows the
   * saved settings at once; when it is not, the view is not touched. Returns false (with the reason in `message`) when nothing was saved.
   */
  editPresetById: (id: string, name: string, criteria: PresetCriteria) => boolean;
  /** Moves a preset in the person's own list of presets. Only its place changes: not the default, not what it holds, not the view. */
  movePresetTo: (id: string, where: MoveWhere) => void;
  deleteById: (id: string) => void;
  makeDefault: (id: string | null) => void;
  dismissMessage: () => void;
}

function fallbackId(): string {
  const c = typeof crypto !== "undefined" ? (crypto as { randomUUID?: () => string }) : undefined;
  return c !== undefined && typeof c.randomUUID === "function" ? c.randomUUID() : "p" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function usePresets({ userId, tab, current, apply, storage, now, newId }: UsePresetsArgs): PresetsController {
  const store = storage === undefined ? browserStorage() : storage;
  const key = presetsKey(userId, tab);
  const [books, setBooks] = useState<Record<string, PresetBook>>({});
  const [applied, setApplied] = useState<Record<string, string | null>>({});
  const [message, setMessage] = useState("");
  const decided = useRef<Set<string>>(new Set());
  const applyRef = useRef(apply);
  applyRef.current = apply;

  // Read the person's presets for this tab once, and apply the default the first time the tab is opened.
  useEffect(() => {
    if (key === null) return;
    const loaded = loadBook(store, userId, tab);
    setBooks((previous) => (key in previous ? previous : { ...previous, [key]: loaded }));
    if (decided.current.has(key)) return;
    decided.current.add(key);
    const fallback = presetOf(loaded, loaded.defaultId);
    setApplied((previous) => ({ ...previous, [key]: fallback === null ? null : fallback.id }));
    if (fallback !== null) applyRef.current(fallback.criteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const book: PresetBook = key === null ? EMPTY_BOOK : books[key] ?? EMPTY_BOOK;
  const appliedId = key === null ? null : applied[key] ?? null;
  const appliedPreset = presetOf(book, appliedId);
  const modified = appliedPreset !== null && !sameCriteria(tab, current, appliedPreset.criteria);
  const stamp = (): string => (now === undefined ? new Date() : now()).toISOString();

  // Keeps a changed book: stored first, so what is shown is what is kept; a store that refuses is reported and nothing changes.
  const commit = useCallback((next: PresetBook): boolean => {
    if (key === null) return false;
    if (!saveBook(store, userId, tab, next)) {
      setMessage("The preset could not be saved in this browser, so nothing was changed.");
      return false;
    }
    setBooks((previous) => ({ ...previous, [key]: next }));
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, userId, tab, store]);

  function markApplied(id: string | null): void {
    if (key !== null) setApplied((previous) => ({ ...previous, [key]: id }));
  }

  return {
    presets: book.presets,
    defaultId: book.defaultId,
    appliedId: appliedPreset === null ? null : appliedPreset.id,
    appliedName: appliedPreset === null ? null : appliedPreset.name,
    modified,
    message,
    canSave: key !== null,
    applyPreset: (id) => {
      const found = presetOf(book, id);
      if (found === null) { setMessage("That preset is no longer there."); return; }
      applyRef.current(found.criteria);
      markApplied(found.id);
      setMessage("");
    },
    // (Applying a preset shows its check mark in the list; no "Applied" message is repeated.)
    savePreset: (name) => {
      const added = addPreset(book, tab, name, current, (newId ?? fallbackId)(), stamp());
      if (!added.ok) { setMessage(added.message); return false; }
      if (!commit(added.book)) return false;
      markApplied(added.preset.id);
      setMessage("Saved the preset " + added.preset.name + ".");
      return true;
    },
    updateApplied: () => {
      if (appliedPreset === null) return;
      const updated = updatePreset(book, tab, appliedPreset.id, current, stamp());
      if (!updated.ok) { setMessage(updated.message); return; }
      if (commit(updated.book)) setMessage("Updated " + appliedPreset.name + " with the view as it is now.");
    },
    resetToApplied: () => {
      if (appliedPreset === null) return;
      applyRef.current(appliedPreset.criteria);
      setMessage("");
    },
    editPresetById: (id, name, criteria) => {
      const edited = editPreset(book, tab, id, name, criteria, stamp());
      if (!edited.ok) { setMessage(edited.message); return false; }
      if (!commit(edited.book)) return false;
      // The applied preset is the view: its new settings are applied now. Any other preset is only saved - the view the person is looking at is not switched.
      if (appliedId === id) applyRef.current(edited.preset.criteria);
      setMessage("Saved changes to " + edited.preset.name + ".");
      return true;
    },
    movePresetTo: (id, where) => {
      const moved = movePreset(book, id, where);
      if (moved !== book) commit(moved);
    },
    renamePresetTo: (id, name) => {
      const renamed = renamePreset(book, id, name, stamp());
      if (!renamed.ok) { setMessage(renamed.message); return false; }
      if (!commit(renamed.book)) return false;
      setMessage("Renamed to " + renamed.preset.name + ".");
      return true;
    },
    deleteById: (id) => {
      const gone = presetOf(book, id);
      if (gone === null) return;
      if (!commit(deletePreset(book, id))) return;
      if (appliedId === id) markApplied(null);
      setMessage("Deleted the preset " + gone.name + ". The view stays as it is.");
    },
    makeDefault: (id) => {
      const target = id === null ? null : presetOf(book, id);
      if (id !== null && target === null) return;
      // The star in the list is the confirmation; no message is repeated.
      commit(setDefault(book, id));
    },
    dismissMessage: () => setMessage(""),
  };
}
